import { ECElementEvent } from 'echarts';
import React, { JSX, useCallback } from 'react';

import { AuctionInsightKey } from '~/src/dash/apiV2/auctionInsights';
import { Sankey } from '~/src/dash/components/charts/Sankey';
import { IEChartsSankeyData, IEChartsSankeyLink } from '~/src/dash/components/charts/Sankey/common';
import { IEChartsSankeyLabelParams } from '~/src/dash/components/charts/Sankey/common';
import {
  formatLinkTooltip,
  formatNodeTooltip,
} from '~/src/dash/components/charts/Sankey/customTooltips';
import { usePublicaTranslation } from '~/src/dash/publicaUI';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { bidderAuctionInsightsNodes } from '~/src/dash/views/bidder/BidderAuctionInsights/config';

interface IProps {
  loadState: LoadingState;
  data: IEChartsSankeyData;
  nodesToExclude: string[];
  hasActiveFilters: boolean;
  handleNodeClick: (params: ECElementEvent) => void;
  handleLinkClick: (params: { data: IEChartsSankeyLink }) => void;
  handleResetDrilldown: () => void;
}

export const AuctionInsightsSankey = React.memo(
  ({
    loadState,
    data,
    nodesToExclude,
    hasActiveFilters,
    handleNodeClick,
    handleLinkClick,
    handleResetDrilldown,
  }: IProps): JSX.Element => {
    const { t } = usePublicaTranslation();

    const labelFormatter = useCallback(
      (params: IEChartsSankeyLabelParams) => {
        const nodeConfig = bidderAuctionInsightsNodes[params.name as AuctionInsightKey];
        const label = nodeConfig?.tKey ? t(nodeConfig.tKey) : params.name;
        const nodeData = params.data.nodeData;
        const depth = params.data.depth ?? 0;
        const percentage: number = (nodeData?.percentage as number) ?? 0;
        const formattedPercentage: string = percentage.toFixed(2);
        const originalValue = (nodeData?.total as number) ?? 0;
        const formattedValue = originalValue.toLocaleString('en-US');
        if (depth === 0) {
          return `{labelName|${label}}\n{labelValue|${formattedValue}}`;
        }
        return `{labelName|${label}}\n{labelValue|${formattedValue} - ${formattedPercentage}%}`;
      },
      [t],
    );

    return (
      <Sankey
        titleTKey='AuctionFlow'
        loadState={loadState}
        nodes={data.nodes}
        links={data.links}
        labelFormatter={labelFormatter}
        nodeTooltipFormatter={formatNodeTooltip}
        linkTooltipFormatter={formatLinkTooltip}
        nodeConfigMap={bidderAuctionInsightsNodes}
        customNoDataMessageTKey={
          hasActiveFilters ? 'NoDataToDisplay' : 'TooltipPleaseSelectAtLeastOneFilterToContinue'
        }
        onNodeClick={handleNodeClick}
        onLinkClick={handleLinkClick}
        onResetDrilldown={handleResetDrilldown}
        isResetActive={nodesToExclude.length > 0}
        messageTKey='SankeyNotice'
      />
    );
  },
);
